"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ALL_CHAINS, RELAY_SOLANA_MAINNET_CHAIN_ID, ZERO_ADDRESS, getAvailableChainIds } from "../../../../anyspend/index.js";
import { Button, useAccountWallet, useTokenBalancesByChain } from "../../../../global-account/react/index.js";
import { cn } from "../../../../shared/utils/index.js";
import { formatTokenAmount } from "../../../../shared/utils/number.js";
import { simpleHashChainToChainName } from "../../../../shared/utils/simplehash.js";
import { TokenSelector } from "@relayprotocol/relay-kit-ui";
import { CheckCircle2, ChevronsUpDown } from "lucide-react";
import { useMemo } from "react";
import { ChainTokenIcon } from "./ChainTokenIcon.js";
export function OrderToken({
  context,
  address,
  chainId,
  setChainId,
  token,
  setToken,
  requiredAmount,
  tokenSelectClassName
}) {
  const {
    wallet
  } = useAccountWallet();
  const chainName = useMemo(() => simpleHashChainToChainName(chainId), [chainId]);
  const {
    nativeTokens,
    fungibleTokens
  } = useTokenBalancesByChain({
    address: wallet?.address || "",
    chainsIds: [chainId],
    enabled: !!wallet?.address && !!chainName
  });
  const {
    formattedBalance: formattedBalance_0,
    hasEnoughBalance: hasEnoughBalance_0
  } = useMemo(() => {
    // Get balance for the selected token
    let balance = null;
    if (token && wallet?.address) {
      if (token.address === ZERO_ADDRESS) {
        // Native token
        const nativeToken = nativeTokens?.find(t => t.chainId === chainId);
        balance = nativeToken?.value ?? null;
      } else {
        // ERC20 token
        const fungibleToken = fungibleTokens?.find(t_0 => t_0.token_address?.toLowerCase() === token.address.toLowerCase());
        balance = fungibleToken?.balance ? BigInt(fungibleToken.balance) : null;
      }
    }
    // Check if balance is sufficient for the required amount
    const hasEnoughBalance = balance && requiredAmount ? balance >= requiredAmount : false;
    const formattedBalance = balance ? formatTokenAmount(balance, token.decimals, 6, false) : null;
    return {
      formattedBalance,
      hasEnoughBalance
    };
  }, [chainId, fungibleTokens, nativeTokens, requiredAmount, token, wallet?.address]);
  return _jsx(TokenSelector, {
    address: address,
    chainIdsFilter: getAvailableChainIds(context),
    context: context,
    fromChainWalletVMSupported: true,
    isValidAddress: true,
    lockedChainIds: getAvailableChainIds(context),
    multiWalletSupportEnabled: true,
    onAnalyticEvent: undefined,
    popularChainIds: [1, 8453, RELAY_SOLANA_MAINNET_CHAIN_ID],
    setToken: token_0 => {
      setChainId(token_0.chainId);
      setToken({
        address: token_0.address,
        chainId,
        decimals: token_0.decimals,
        metadata: {
          logoURI: token_0.logoURI
        },
        name: token_0.name,
        symbol: token_0.symbol
      });
    },
    supportedWalletVMs: ["evm", "svm"],
    token: undefined,
    trigger: _jsxs(Button, {
      variant: "outline",
      role: "combobox",
      className: cn("bg-b3-react-background border-as-stroke flex h-auto w-fit shrink-0 items-center justify-center gap-2 rounded-xl border-2 px-2 py-1 pr-2 text-center", tokenSelectClassName),
      children: [token.metadata.logoURI ? _jsx(ChainTokenIcon, {
        chainUrl: ALL_CHAINS[chainId].logoUrl,
        tokenUrl: token.metadata.logoURI,
        className: "h-8 min-h-8 w-8 min-w-8"
      }) : _jsx("div", {
        className: "h-8 w-8 rounded-full bg-gray-700"
      }), _jsxs("div", {
        className: "flex flex-col items-start gap-0",
        children: [_jsxs("div", {
          className: "text-as-primary font-semibold",
          children: [token.symbol, " on ", ALL_CHAINS[chainId].name]
        }), formattedBalance_0 && _jsxs("div", {
          className: "flex items-center gap-1",
          children: [_jsxs("div", {
            className: "text-as-primary/50 text-xs",
            children: [formattedBalance_0 || "--", " ", token.symbol]
          }), hasEnoughBalance_0 && _jsx(CheckCircle2, {
            className: "text-as-brand -mt-0.5 h-4 w-4"
          })]
        })]
      }), _jsx(ChevronsUpDown, {
        className: "h-4 w-4 shrink-0 opacity-70"
      })]
    })
  }, undefined);
}